import { logger } from '@/core/logger'
import { APP_VERSION } from './version'
import { markUpdateAttempt } from './update-attempt'

/**
 * The waiting-service-worker activator, handed over by `registerSW` in
 * main.tsx. Held because it is the ONLY graceful way out of a waiting worker
 * under `registerType: 'prompt'` (which vite.config.ts sets deliberately — the
 * FCM SW shares the `/` scope). It used to be discarded, so the sole route to
 * a new bundle was the scorched-earth unregister below, and a reload that
 * outran it left the worker waiting and the prompt returning.
 */
let swUpdate: ((reload?: boolean) => Promise<void>) | null = null
export function registerSwUpdater(fn: (reload?: boolean) => Promise<void>): void {
  swUpdate = fn
}

/**
 * Hard refresh the PWA, bypassing every layer of browser cache.
 *
 * 1. Unregister every service worker so the old app shell is evicted.
 * 2. Delete every Cache Storage entry so the next load fetches fresh
 *    assets.
 * 3. Navigate to the same path with a cache-busting `?_v=<ts>` query param
 *    via `location.replace()` — bypasses the HTTP disk cache and avoids
 *    pushing the pre-reload URL onto history.
 *
 * Cleanup is capped at a 3s deadline so a hung SW (e.g. a Chrome instance
 * where `getRegistrations()` never resolves) can't swallow the click — the
 * navigation always fires after the deadline even if cleanup is mid-flight.
 * Each step is best-effort: errors are logged and swallowed, the reload
 * still happens.
 */
// EACH STEP GETS ITS OWN BUDGET. They used to SHARE one 3 s deadline, so a
// slow cache wipe could consume the whole allowance and the unregister lost the
// race — the page then reloaded with the old worker still registered, the new
// one sat waiting behind it, and the prompt came back. That sharing was the
// bug; the number was never the problem.
//
// Both stay BOUNDED rather than awaited outright: `getRegistrations()` hangs
// forever on some Chrome builds, and a user whose click does NOTHING is worse
// off than one who sees the prompt twice. `force-update.test.ts` pins that
// case — an earlier attempt at this fix removed the bound and broke it.
const UNREGISTER_DEADLINE_MS = 3000
const CLEANUP_DEADLINE_MS = 3000

export async function forceUpdate(): Promise<void> {
  // Remember we tried, BEFORE navigating. If we come back on the same version
  // the caller must stop offering the button — see `update-attempt.ts`.
  markUpdateAttempt(APP_VERSION)

  // GRACEFUL FIRST. Under `prompt` a waiting worker never activates on its
  // own; this is what tells it to. It also reloads, so if it succeeds nothing
  // below runs.
  if (swUpdate) {
    try {
      await swUpdate(true)
    } catch (err) {
      logger.w('forceUpdate: updateSW failed, falling back to unregister', err)
    }
  }

  // UNREGISTRATION IS THE PART THAT MATTERS, so it is awaited rather than
  // raced. Reloading with the old worker still registered is what produced the
  // returning prompt: the stale worker controls the fresh page, the new one
  // installs behind it and waits, and onNeedRefresh fires again.
  const unregister = (async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map((r) => r.unregister()))
      }
    } catch (err) {
      logger.w('forceUpdate: could not unregister service workers', err)
    }
  })()
  await Promise.race([
    unregister,
    new Promise<void>((resolve) => setTimeout(resolve, UNREGISTER_DEADLINE_MS)),
  ])

  // Cache clearing is optional — the new worker re-precaches — so this is the
  // only step worth time-boxing. A hung `caches.keys()` must not swallow the
  // click.
  const clearCaches = (async () => {
    try {
      // Safari private browsing: `'caches' in window` is true but
      // `window.caches` is null, so check the value itself (Sentry
      // CLUBMANAGER-PWA-4).
      if (window.caches && typeof window.caches.keys === 'function') {
        const keys = await window.caches.keys()
        await Promise.all(keys.map((k) => window.caches.delete(k)))
      }
    } catch (err) {
      logger.w('forceUpdate: could not clear caches', err)
    }
  })()
  await Promise.race([
    clearCaches,
    new Promise<void>((resolve) => setTimeout(resolve, CLEANUP_DEADLINE_MS)),
  ])

  const { origin, pathname } = window.location
  window.location.replace(`${origin}${pathname}?_v=${Date.now()}`)
}
